import React, { useState, useEffect, useContext } from "react";
import Footer from "../sub-components/Footer.tsx";
import HeaderBanner from "../sub-components/HeaderBanner.tsx";
import { Typography, Box, Skeleton, Pagination, PaginationItem, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import { HttpContext } from "../../api-services/HttpService";

const baseTextStyle = {
    fontFamily: "Roboto regular, Helvetica, Arial, sans-serif"
};

const MainList = styled('ul')(({ theme }) => ({
    paddingLeft: "1.5rem",
    marginBottom: "0.5rem",
    fontFamily: "Roboto regular, Helvetica, Arial, sans-serif",
    '& > li::marker': {
        color: "#b2b1b1",
        fontSize: "1.3rem"
    }
}));

const NestedList = styled('ul')(({ theme }) => ({
    paddingLeft: "1.5rem",
    marginBottom: "0.5rem",
    listStyleType: "none", // Remove bullets for nested lists
    fontFamily: "Roboto regular, Helvetica, Arial, sans-serif"
}));

const ListItem = styled('li')(({ theme }) => ({
    marginBottom: "0.5rem",
    fontFamily: "Roboto regular, Helvetica, Arial, sans-serif",
    fontSize:"14px"
}));

const NestedListItem = styled('li')(({ theme }) => ({
    marginBottom: "0.25rem",
    fontFamily: "Roboto regular, Helvetica, Arial, sans-serif",
    fontSize:"14px"
}));

const NestedListItemComponent = ({ item, isNested = false }) => {
    const ItemComponent = isNested ? NestedListItem : ListItem;
    
    return (
        <ItemComponent>
            {item.text}
            {item.subItems && item.subItems.length > 0 && (
                <NestedList>
                    {item.subItems.map((subItem, index) => (
                        typeof subItem === 'string' 
                            ? <NestedListItem key={index}>{subItem}</NestedListItem>
                            : <NestedListItemComponent key={index} item={subItem} isNested={true} />
                    ))}
                </NestedList>
            )}
        </ItemComponent>
    );
};

const parseJsonString = (jsonString) => {
    try {
        if (typeof jsonString === 'string' && jsonString.trim().startsWith('[')) {
            return JSON.parse(jsonString);
        }
    } catch (error) {
        console.error("Error parsing JSON string:", error);
    }
    return [];
};

const ReleaseNoteTemplate = ({ releaseDate, releaseVersion, title, newFeatures, bugfixes }) => {
    const parsedNewFeatures = parseJsonString(newFeatures);
    const parsedBugfixes = parseJsonString(bugfixes);
    
    return (
        <div style={baseTextStyle}>
            <div className="d-flex align-items-center gap-2 mb-3">
                <Typography sx={{fontSize:"12px"}}>Release Date: <b>{releaseDate}</b></Typography>
                <div className="vr"></div>
                <Typography sx={{fontSize:"12px"}}>Release Version: <b>{releaseVersion}</b></Typography>
            </div>
            <Typography className="mb-2" sx={{fontSize:"16px",fontFamily:'"Roboto regular" ,"Helvetica","Arial","sans-serif"'}}><b>{title}</b></Typography>
            <Typography sx={{ textDecoration: "underline",color:"#474747",fontWeight:"600",fontSize:"14px",fontFamily:'"Roboto regular" ,"Helvetica","Arial","sans-serif"' }}>New</Typography>
            <MainList>
                {parsedNewFeatures.map((feature, index) => (
                    <NestedListItemComponent key={index} item={feature} />
                ))}
            </MainList>
            <br />
            <Typography sx={{ textDecoration: "underline",color:"#474747",fontWeight:"600",fontSize:"14px",fontFamily:'"Roboto regular" ,"Helvetica","Arial","sans-serif"' }}>Bugfixes</Typography>
            <MainList>
                {parsedBugfixes.map((bug, index) => (
                    <NestedListItemComponent key={index} item={bug} />
                ))}
            </MainList>
        </div>);
}

// Function to calculate pagination start and end values
const calculatePagination = (totalItems, itemsPerPage, currentPage) => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    let end = currentPage * itemsPerPage;
    if (end > totalItems) {
        end = totalItems;
    }
    return { start, end };
};

const ReleaseNotesPage = () => {
    const { GET } = useContext(HttpContext);

    const [releaseNotes, setReleaseNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination states
    const [page, setPage] = useState(1);
    const [pageInput, setPageInput] = useState(1);
    const [itemsPerPage] = useState(10);
    const [numOfPages, setNumOfPages] = useState(0);
    const [paginatedNotes, setPaginatedNotes] = useState([]);

    const fetchReleaseNotes = async () => {
        setLoading(true);
        try {
            const response = await GET('/data/releaseNotes', {});
            if (response.data && response.data.ReleaseNotes) {
                const notes = response.data.ReleaseNotes.map(note => ({
                    releaseDate: note.releasedate,
                    releaseVersion: note.releaseversion,
                    title: note.releasetitle,
                    newFeatures: note.releasenewfeatures,
                    bugfixes: note.releasebugfixes
                }));
                setReleaseNotes(notes);
                
                // Calculate number of pages
                let totalPages = Math.ceil(notes.length / itemsPerPage);
                setNumOfPages(totalPages);
            } else {
                setReleaseNotes([]);
                setNumOfPages(0);
            }
            
            setLoading(false);
        } catch (err) {
            console.error("Error fetching release notes:", err);
            setError("Failed to load release notes. Please try again later.");
            setLoading(false);
        }
    };

    // Fetch release notes on component mount
    useEffect(() => {
        fetchReleaseNotes();
    }, []);

    // Update paginated notes when page changes or release notes change
    useEffect(() => {
        if (releaseNotes.length > 0) {
            const startIndex = (page - 1) * itemsPerPage;
            const endIndex = Math.min(startIndex + itemsPerPage, releaseNotes.length);
            setPaginatedNotes(releaseNotes.slice(startIndex, endIndex));
        } else {
            setPaginatedNotes([]);
        }
    }, [page, releaseNotes, itemsPerPage]);

    // Handle page change
    const onPageChange = (event, value) => {
        setPage(value);
        setPageInput(value);
    };

    // Handle page input change
    const handlePageInputChange = (event) => {
        const value = event.target.value;
        // Only allow numbers
        if (value === '' || /^\d+$/.test(value)) {
            const numValue = parseInt(value) || '';
            setPageInput(numValue);
        }
    };
    
    // Handle page input key press (Enter)
    const handlePageInputKeyPress = (event) => {
        if (event.key === 'Enter') {
            // Return early if pageInput is empty or not a number
            if (!pageInput || isNaN(pageInput)) {
                // Reset to current page
                setPageInput(page);
                return;
            }
    
            let newPage = parseInt(pageInput);
            if (newPage < 1) {
                newPage = 1;
            } else if (newPage > numOfPages) {
                newPage = numOfPages;
            }
            setPage(newPage);
            setPageInput(newPage);
        }
    };
    
    // Handle page input blur
    const handlePageInputBlur = () => {
        if (!pageInput || isNaN(pageInput)) {
            setPageInput(page);
        }
    };

    return (
        <div className="summary-page" style={baseTextStyle}>
            {/* header and navslider present on every page */}
            <HeaderBanner />
            <div className="container-fluid d-flex flex-column">
                <Typography variant="h5" style={{ margin: '0.5em 5em 0.5em 6em' }}>
                    Release Notes
                </Typography>
                <div className="p-3" style={{ borderRadius: '12px', background: '#fff', margin: '0 8em', overflowY: 'auto', height: "calc(100vh - 200px)",maxHeight: "calc(100vh - 200px)"}}>
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{height:"100%"}}>
                             <Skeleton variant="rectangular" width='100%' height='100%' sx={{ borderRadius: '5px' }}/>
                        </div>
                    ) : error ? (
                        <div className="d-flex justify-content-center align-items-center" style={{height:"100%"}}>
                            <Typography>{error}</Typography>
                        </div>
                    ) : (
                        releaseNotes.length > 0 ? (
                            <>
                                {paginatedNotes.map((note, index) => (
                                    <React.Fragment key={index}>
                                        <ReleaseNoteTemplate 
                                            releaseDate={note.releaseDate}
                                            releaseVersion={note.releaseVersion}
                                            title={note.title}
                                            newFeatures={note.newFeatures}
                                            bugfixes={note.bugfixes}
                                        />
                                        {index < paginatedNotes.length - 1 && <hr />}
                                    </React.Fragment>
                                ))}
                                
                                {/* Pagination */}
                                {numOfPages > 1 && (
                                    <div>
                                        <hr/>
                                        <div className="d-flex justify-content-end align-items-center mt-2">
                                            <Typography
                                                fontSize={"12px"}
                                                paddingRight={"8px"}
                                                fontWeight={"400"}
                                                lineHeight={"16px"}
                                            >
                                                {calculatePagination(releaseNotes.length, itemsPerPage, page).start +
                                                    "-" +
                                                    calculatePagination(releaseNotes.length, itemsPerPage, page).end +
                                                    " of " +
                                                    releaseNotes.length +
                                                    " items"}
                                            </Typography>
                                            <Pagination
                                                page={page}
                                                count={numOfPages}
                                                variant="outlined"
                                                shape="rounded"
                                                onChange={onPageChange}
                                                renderItem={(item) => (
                                                    <PaginationItem
                                                        {...item}
                                                        components={{
                                                            previous: () => (
                                                                <Typography fontSize={"12px"}>Previous</Typography>
                                                            ),
                                                            next: () => (
                                                                <Typography fontSize={"12px"}>Next</Typography>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            />
                                            <Box className="d-flex align-items-center ms-2 me-1">
                                                <Typography fontSize={"12px"} className="me-2">
                                                    Page
                                                </Typography>
                                                <TextField
                                                    size="small"
                                                    value={pageInput}
                                                    onChange={handlePageInputChange}
                                                    onKeyPress={handlePageInputKeyPress}
                                                    onBlur={handlePageInputBlur}
                                                    inputProps={{
                                                        style: { 
                                                            padding: '7px 6px',
                                                            width: '50px',
                                                            textAlign: 'center',
                                                            fontSize: '12px'
                                                        },
                                                        'aria-label': 'Jump to page'
                                                    }}
                                                    sx={{ 
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 1,
                                                            borderColor: '#00A6CA',
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#00A6CA'
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#00A6CA'
                                                            }
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#00A6CA',
                                                            '&:hover': {
                                                                color: '#00A6CA'
                                                            }
                                                        },
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#00A6CA'
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#00A6CA'
                                                        },
                                                        '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#BDBDBD'
                                                        },
                                                        '& .Mui-disabled input': {
                                                            color: '#BDBDBD',
                                                            backgroundColor: '#FCFCFC'
                                                        }
                                                    }}
                                                />
                                                <Typography fontSize={"12px"} sx={{ ml: 1 }}>
                                                    of {numOfPages}
                                                </Typography>
                                            </Box>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="d-flex justify-content-center align-items-center" style={{height:"100%"}}>
                                <Typography>No release notes available.</Typography>
                            </div>
                        )
                    )}
                </div>
            </div>
            <Footer cssClassName="footer-main-comp" />
        </div>
    );
};

export default ReleaseNotesPage;